// Pure geometry for the plan Gantt: turn dates into percentages along a shared
// time window, so every bar, tick, and marker in one chart uses the same x-axis.

import type { LongTermGoalInput, PlanInput, TaskInput } from "@/types";

export type TimeWindow = { startMs: number; endMs: number };
export type Bar = { leftPct: number; widthPct: number };
export type Tick = { label: string; leftPct: number };

const PCT_MAX = 100;
/** A one-day task would otherwise be invisible on a six-month axis. */
const MIN_BAR_PCT = 1.5;
/** Above this many months, label every other one so the ticks stay readable. */
const MAX_DENSE_TICKS = 8;

/**
 * The chart's x-axis: the goal's window, widened to cover the plan and any task
 * that falls outside it — so a step running past its deadline is still visible.
 * Null when nothing here is dated.
 */
export function chartWindow(
  goal: LongTermGoalInput,
  plan: PlanInput,
  tasks: TaskInput[],
): TimeWindow | null {
  const starts = millis([
    goal.startAt,
    plan.startAt,
    ...tasks.map((task) => task.plannedStartAt),
  ]);
  const ends = millis([
    goal.deadlineAt,
    plan.endAt,
    ...tasks.map((task) => task.plannedEndAt),
  ]);
  if (starts.length === 0 || ends.length === 0) return null;

  const startMs = Math.min(...starts);
  const endMs = Math.max(...ends);
  return endMs > startMs ? { startMs, endMs } : null;
}

/** A task's bar, or null when it has no planned window to draw. */
export function taskBar(window: TimeWindow, task: TaskInput): Bar | null {
  return spanBar(window, task.plannedStartAt, task.plannedEndAt);
}

/** The goal's own span, drawn as the chart's summary band. */
export function goalBar(window: TimeWindow, goal: LongTermGoalInput): Bar | null {
  return spanBar(window, goal.startAt, goal.deadlineAt);
}

/** Where a date sits on the axis, or null when it falls outside the window. */
export function markerPct(window: TimeWindow, iso: string | undefined): number | null {
  const at = toMs(iso);
  if (at === null || at < window.startMs || at > window.endMs) return null;
  return pctOf(window, at);
}

function spanBar(
  window: TimeWindow,
  startIso: string | undefined,
  endIso: string | undefined,
): Bar | null {
  const start = toMs(startIso);
  const end = toMs(endIso);
  if (start === null || end === null) return null;

  const leftPct = clampPct(pctOf(window, start));
  const widthPct = Math.max(MIN_BAR_PCT, clampPct(pctOf(window, end)) - leftPct);
  return { leftPct, widthPct: Math.min(widthPct, PCT_MAX - leftPct) };
}

/** Where "now" sits on the axis, or null when today is outside the window. */
export function nowMarkerPct(window: TimeWindow, now: number): number | null {
  if (now < window.startMs || now > window.endMs) return null;
  return pctOf(window, now);
}

/** One tick per month boundary inside the window, thinned out when crowded. */
export function monthTicks(window: TimeWindow): Tick[] {
  const ticks: Tick[] = [];
  const cursor = new Date(window.startMs);
  cursor.setDate(1);
  cursor.setHours(0, 0, 0, 0);

  while (cursor.getTime() <= window.endMs) {
    const at = cursor.getTime();
    if (at >= window.startMs) {
      ticks.push({
        label: cursor.toLocaleDateString(undefined, { month: "short" }),
        leftPct: pctOf(window, at),
      });
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }

  if (ticks.length <= MAX_DENSE_TICKS) return ticks;
  return ticks.filter((_, index) => index % 2 === 0);
}

function pctOf(window: TimeWindow, at: number): number {
  return ((at - window.startMs) / (window.endMs - window.startMs)) * PCT_MAX;
}

function clampPct(value: number): number {
  return Math.min(PCT_MAX, Math.max(0, value));
}

function millis(dates: (string | undefined)[]): number[] {
  return dates.map(toMs).filter((ms): ms is number => ms !== null);
}

function toMs(iso: string | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
}
